import { doc, getDoc, updateDoc } from 'firebase/firestore'
import { db } from '../../../../firebase'
import { authFetch } from '../../../utils/cloudFunctionsAuth'

export type GraphEmailBody = {
  contentType: 'html' | 'text'
  content: string
  fetchedAt: number
}

export type GraphEmailPrefer = 'text' | 'html'

const MAX_CACHE_ITEMS = 60
const TTL_MS = 10 * 60 * 1000 // 10 minutes
const MAX_PERSIST_CHARS = 750_000 // keep well under Firestore 1MiB doc limit

// Simple LRU via Map insertion order
const cache = new Map<string, GraphEmailBody>()
const inflight = new Map<string, Promise<GraphEmailBody>>()

// Track emails whose graph.id is stale (Graph returned 404).
// These are cached permanently for the session so we don't keep retrying.
const notFoundIds = new Set<string>()

// Circuit breaker to stop prefetching when backend is failing
// NOTE: 404s (stale graph.id) do NOT count toward this — only real service errors do.
let consecutiveErrors = 0
let circuitBreakerResetTime = 0
const MAX_CONSECUTIVE_ERRORS = 3 // After 3 errors, stop prefetching
const CIRCUIT_BREAKER_COOLDOWN_MS = 60_000 // Wait 1 minute before retrying

// Concurrency limiter - only allow N prefetches at once
const MAX_CONCURRENT_PREFETCHES = 2
let activePrefetches = 0

function isCircuitOpen(): boolean {
  if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
    if (Date.now() < circuitBreakerResetTime) {
      return true // Circuit is open, don't make requests
    }
    // Cooldown expired, reset and allow one retry
    consecutiveErrors = 0
  }
  return false
}

function recordSuccess() {
  consecutiveErrors = 0
}

function recordError() {
  consecutiveErrors++
  if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
    circuitBreakerResetTime = Date.now() + CIRCUIT_BREAKER_COOLDOWN_MS
    console.warn('[graphEmailBodyCache] Circuit breaker OPEN - pausing prefetch for 1 minute')
  }
}

function normalizePrefer(prefer?: GraphEmailPrefer): GraphEmailPrefer {
  return prefer === 'html' ? 'html' : 'text'
}

function keyForEmailId(emailId: string, prefer?: GraphEmailPrefer): string {
  const id = String(emailId || '').trim()
  if (!id) return ''
  return `${id}|${normalizePrefer(prefer)}`
}

export function getCachedGraphEmailBody(emailId: string, prefer: GraphEmailPrefer = 'text'): GraphEmailBody | null {
  const key = keyForEmailId(emailId, prefer)
  if (!key) return null
  const v = cache.get(key)
  if (!v) return null

  if (Date.now() - v.fetchedAt > TTL_MS) {
    cache.delete(key)
    return null
  }

  // refresh LRU
  cache.delete(key)
  cache.set(key, v)
  return v
}

function setCached(emailId: string, prefer: GraphEmailPrefer, body: GraphEmailBody) {
  const key = keyForEmailId(emailId, prefer)
  if (!key) return
  cache.delete(key)
  cache.set(key, body)
  while (cache.size > MAX_CACHE_ITEMS) {
    const oldestKey = cache.keys().next().value
    if (!oldestKey) break
    cache.delete(oldestKey)
  }
}

// Custom error to distinguish Graph 404 (stale ID) from real service errors
class GraphNotFoundError extends Error {
  constructor(message: string) { super(message); this.name = 'GraphNotFoundError' }
}

async function fetchGraphEmailBody(emailId: string, prefer: GraphEmailPrefer): Promise<GraphEmailBody> {
  const resp = await authFetch('https://us-central1-web-construct-app.cloudfunctions.net/getGraphEmailBody', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ emailId, prefer }),
  })
  const data = await resp.json().catch(() => ({}))
  if (!resp.ok || data?.ok !== true) {
    // 404 = email no longer exists in Graph (stale graph.id after move/delete)
    // (kept for backwards-compat in case cloud function hasn't been redeployed yet)
    if (resp.status === 404) {
      throw new GraphNotFoundError(data?.error || 'Email no longer exists in mailbox')
    }
    throw new Error(data?.error || `Failed to fetch full email body (HTTP ${resp.status})`)
  }

  // Cloud function now returns 200 + notFound:true for stale graph.ids
  // (instead of 404, so the browser doesn't log red errors)
  if (data?.notFound === true) {
    throw new GraphNotFoundError('Email no longer exists in mailbox')
  }

  const ct = (typeof data?.contentType === 'string' && data.contentType)
    ? String(data.contentType).toLowerCase()
    : null

  const content = typeof data?.content === 'string'
    ? data.content
    : (ct === 'text'
      ? (typeof data?.text === 'string' ? data.text : '')
      : (typeof data?.html === 'string' ? data.html : ''))

  const contentType: 'html' | 'text' = ct === 'text' ? 'text' : 'html'

  return { contentType, content: String(content || ''), fetchedAt: Date.now() }
}

// Ingested-body fast path: read the body straight from email_bodies/{emailId}
// (written by the Graph sync). One small Firestore read — fast, cacheable, no
// Graph round-trip. Returns null when there's no ingested body yet (an older or
// not-yet-synced email), so the caller falls back to the Graph cloud function.
async function fetchBodyFromFirestore(emailId: string, prefer: GraphEmailPrefer): Promise<GraphEmailBody | null> {
  try {
    const snap = await getDoc(doc(db, 'email_bodies', emailId))
    if (!snap.exists()) return null
    const d: any = snap.data() || {}
    const html = typeof d.html === 'string' ? d.html : ''
    const text = typeof d.text === 'string' ? d.text : ''
    const now = Date.now()
    if (prefer === 'text') {
      if (text) return { contentType: 'text', content: text, fetchedAt: now }
      if (html) return { contentType: 'html', content: html, fetchedAt: now }
    } else {
      if (html) return { contentType: 'html', content: html, fetchedAt: now }
      if (text) return { contentType: 'text', content: text, fetchedAt: now }
    }
    return null
  } catch {
    return null
  }
}

// Resolve a body: the ingested Firestore doc first, Graph cloud function fallback.
async function fetchBody(emailId: string, prefer: GraphEmailPrefer): Promise<GraphEmailBody> {
  const stored = await fetchBodyFromFirestore(emailId, prefer)
  if (stored) return stored
  return fetchGraphEmailBody(emailId, prefer)
}

// ── Prefetch queue ──────────────────────────────────────────────────────────
// warmGraphEmailBodyCache used to DROP every request past the concurrency cap
// (early return, no queue), so asking to warm a screenful of N rows only ever
// warmed the first 2 and silently discarded the rest. The feed needs the whole
// VISIBLE working set warmed reliably, so prefetch requests now QUEUE and the
// queue drains at the concurrency cap. Cached / inflight / known-404 ids are
// skipped at both enqueue and dequeue time; the circuit breaker still gates
// genuine outages.
type PrefetchItem = { emailId: string; prefer: GraphEmailPrefer; key: string }
const prefetchQueue: PrefetchItem[] = []
const queuedKeys = new Set<string>()
const MAX_PREFETCH_QUEUE = 48 // best-effort cap so a long scroll can't grow it unbounded

// True when we already have (fresh) or are already fetching this body — i.e. a
// prefetch would be wasted. Intentionally does NOT touch LRU order (a prefetch
// should never promote an entry the way a real open does).
function alreadyHaveOrPending(emailId: string, key: string): boolean {
  if (inflight.has(key)) return true
  if (notFoundIds.has(emailId)) return true
  const cached = cache.get(key)
  return !!(cached && Date.now() - cached.fetchedAt <= TTL_MS)
}

function drainPrefetchQueue(): void {
  while (activePrefetches < MAX_CONCURRENT_PREFETCHES && prefetchQueue.length > 0) {
    // Circuit open (backend failing): stop draining. A later warm() call
    // re-kicks the drain once isCircuitOpen()'s cooldown has elapsed.
    if (isCircuitOpen()) return
    const item = prefetchQueue.shift()!
    queuedKeys.delete(item.key)
    // State may have changed while queued (opened directly, warmed via the other
    // prefer, expired) — re-check before spending a request.
    if (alreadyHaveOrPending(item.emailId, item.key)) continue

    activePrefetches++
    const p = fetchBody(item.emailId, item.prefer)
      .then((body) => { setCached(item.emailId, item.prefer, body); recordSuccess(); return body })
    inflight.set(item.key, p)
    p.catch((e) => {
      // 404 = stale graph.id: remember it so we never retry, but it is NOT an
      // outage — don't trip the circuit breaker. Real errors (500/network) do.
      if (e instanceof GraphNotFoundError) notFoundIds.add(item.emailId)
      else recordError()
    }).finally(() => {
      activePrefetches--
      inflight.delete(item.key)
      drainPrefetchQueue()
    })
  }
}

/**
 * Best-effort warm the in-memory cache for an email body. Enqueues the request
 * and returns immediately; the queue drains at MAX_CONCURRENT_PREFETCHES. Safe
 * to call for a whole screenful of rows at once — duplicates, already-cached,
 * inflight and known-stale ids are skipped. Never throws and never surfaces to
 * the UI (actual opens use getOrFetchGraphEmailBody, which still throws).
 */
export function warmGraphEmailBodyCache(emailId: string, prefer: GraphEmailPrefer = 'text'): void {
  const key = keyForEmailId(emailId, prefer)
  if (!key) return
  if (queuedKeys.has(key)) return
  if (alreadyHaveOrPending(emailId, key)) return
  // Bound the queue: drop the OLDEST pending request (least likely to still be
  // on screen) rather than the one just asked for.
  if (prefetchQueue.length >= MAX_PREFETCH_QUEUE) {
    const dropped = prefetchQueue.shift()
    if (dropped) queuedKeys.delete(dropped.key)
  }
  prefetchQueue.push({ emailId, prefer, key })
  queuedKeys.add(key)
  drainPrefetchQueue()
}

export async function getOrFetchGraphEmailBody(emailId: string, prefer: GraphEmailPrefer = 'text'): Promise<GraphEmailBody> {
  const key = keyForEmailId(emailId, prefer)
  if (!key) throw new Error('Missing emailId')

  // If we already know this email has a stale graph.id, return empty body immediately
  if (notFoundIds.has(emailId)) {
    return { contentType: 'text', content: '', fetchedAt: Date.now() }
  }

  const cached = getCachedGraphEmailBody(emailId, prefer)
  if (cached) return cached

  const existingInflight = inflight.get(key)
  if (existingInflight) return await existingInflight

  const p = (async () => {
    const body = await fetchBody(emailId, prefer)
    setCached(emailId, prefer, body)
    return body
  })()
  inflight.set(key, p)
  try {
    return await p
  } catch (e) {
    if (e instanceof GraphNotFoundError) {
      notFoundIds.add(emailId)
      return { contentType: 'text', content: '', fetchedAt: Date.now() }
    }
    throw e
  } finally {
    inflight.delete(key)
  }
}

export async function persistGraphEmailBodyToFirestore(emailId: string, body: GraphEmailBody): Promise<void> {
  const id = String(emailId || '').trim()
  if (!id) return
  const content = body.content || ''
  if (!content) return
  if (content.length > MAX_PERSIST_CHARS) return

  const ref = doc(db, 'emails', id)
  if (body.contentType === 'text') {
    await updateDoc(ref, { text: content, html: null })
  } else {
    await updateDoc(ref, { html: content })
  }
}


